import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Category, Translation, initialCategory } from '../models/category'
import { createCategory, deleteCategory, getCategories, updateCategory, getCategory } from '../api/categoriesApi'
import { KTSVG } from '../../../../../_dashboard/helpers/components/KTSVG'
import { toast } from 'react-toastify'
import { Tooltip, OverlayTrigger, Form } from 'react-bootstrap'
import { AVAILABLE_LANGUAGES } from '../constants'

const CategoriesTab: React.FC = () => {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState<Category[]>([])
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const pageSize = 1000

  const loadCategories = async () => {
    try {
      setLoading(true)
      const response = await getCategories(page, pageSize)
      setCategories(response.data || [])
      setTotalPages(response.payload.pagination.totalPages || 0)
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_CATEGORY.ERROR.LOAD' }))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCategories()
  }, [page])

  const categorySchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    description: Yup.string().required('Description is required'),
    status: Yup.string().oneOf(['active', 'inactive']).required('Status is required'),
    translations: Yup.array().of(
      Yup.object().shape({
        language: Yup.string().required('Language is required'),
        title: Yup.string().required('Title is required'),
        description: Yup.string().required('Description is required'),
      })
    ),
  })

  const formik = useFormik({
    initialValues: editingCategory || initialCategory,
    validationSchema: categorySchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        if (editingCategory?._id) {
          const { _id, __v, translations, ...rest } = values as any
          const cleanTranslations = (translations || []).map((t: any) => {
            const { _id, __v, ...tRest } = t
            return tRest
          })
          const cleanValues = { ...rest, translations: cleanTranslations }
          await updateCategory(editingCategory._id, cleanValues)
          toast.success(intl.formatMessage({ id: 'TOUR_CATEGORY.SUCCESS.UPDATE' }))
        } else {
          await createCategory(values)
          toast.success(intl.formatMessage({ id: 'TOUR_CATEGORY.SUCCESS.CREATE' }))
        }
        resetForm()
        setEditingCategory(null)
        setShowModal(false)
        loadCategories()
      } catch (error) {
        toast.error(intl.formatMessage({ id: editingCategory?._id ? 'TOUR_CATEGORY.ERROR.UPDATE' : 'TOUR_CATEGORY.ERROR.CREATE' }))
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleDelete = async () => {
    if (!categoryToDelete) return

    try {
      setIsDeleting(true)
      await deleteCategory(categoryToDelete)
      toast.success(intl.formatMessage({ id: 'TOUR_CATEGORY.SUCCESS.DELETE' }))
      loadCategories()
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_CATEGORY.ERROR.DELETE' }))
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
      setCategoryToDelete(null)
    }
  }

  const handleEdit = async (category: Category) => {
    try {
      setLoading(true)
      const response = await getCategory(category._id!)
      setEditingCategory(response.data)
      formik.setValues(response.data)
      setShowModal(true)
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_CATEGORY.ERROR.LOAD' }))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setEditingCategory(null)
    formik.resetForm()
    setShowModal(false)
  }

  const handleAddNew = () => {
    setEditingCategory(null)
    formik.resetForm()
    setShowModal(true)
  }

  const handleAddTranslation = () => {
    const currentTranslations = formik.values.translations || []
    const usedLanguages = new Set(currentTranslations.map(t => t.language))
    const availableLanguage = AVAILABLE_LANGUAGES.find(lang => !usedLanguages.has(lang))
    
    if (availableLanguage) {
      formik.setFieldValue('translations', [
        ...currentTranslations,
        { language: availableLanguage, title: '', description: '' }
      ])
    } else {
      toast.warning(intl.formatMessage({ id: 'TOUR_CATEGORY.ERROR.NO_AVAILABLE_LANGUAGES' }))
    }
  }

  const handleRemoveTranslation = (index: number) => {
    const currentTranslations = [...formik.values.translations]
    currentTranslations.splice(index, 1)
    formik.setFieldValue('translations', currentTranslations)
  }

  return (
    <div className='card card-custom'>
      {/* Header with Add Button */}
      <div className='card-header border-0 pt-6'>
        <div className='card-title'></div>
        <div className='card-toolbar'>
          <div className='d-flex justify-content-end'>
            <button type='button' className='btn btn-primary' onClick={handleAddNew}>
              <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
              {intl.formatMessage({ id: 'TOUR_CATEGORY.ADD_NEW' })}
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className='card-body pt-0'>
        {loading ? (
          <div className='d-flex justify-content-center align-items-center min-h-300px'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className='table-responsive'>
              <table className='table align-middle table-row-dashed fs-6 gy-5'>
                <thead>
                  <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                    <th>{intl.formatMessage({ id: 'TOUR_CATEGORY.TITLE' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_CATEGORY.DESCRIPTION' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_CATEGORY.TRANSLATIONS' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_CATEGORY.STATUS' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_CATEGORY.ACTIONS' })}</th>
                  </tr>
                </thead>
                <tbody className='text-gray-600 fw-semibold'>
                  {categories.map((category) => (
                    <tr key={category._id}>
                      <td>{category.title}</td>
                      <td>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>{category.description}</Tooltip>}
                        >
                          <span className='text-truncate d-inline-block' style={{ maxWidth: '200px' }}>
                            {category.description}
                          </span>
                        </OverlayTrigger>
                      </td>
                      <td>
                        <div className='d-flex flex-wrap gap-2'>
                          {category.translations.map((translation) => (
                            <span key={translation.language} className='badge badge-light-primary'>
                              {translation.language.toUpperCase()}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`badge badge-light-${
                            category.status === 'active' ? 'success' : 'danger'
                          } fw-bold`}
                        >
                          {category.status}
                        </span>
                      </td>
                      <td>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.EDIT'})}</Tooltip>}
                        >
                          <button
                            className='btn btn-icon btn-light-primary btn-sm me-1'
                            onClick={() => handleEdit(category)}
                          >
                            <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                          </button>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.DELETE'})}</Tooltip>}
                        >
                          <button
                            className='btn btn-icon btn-light-danger btn-sm'
                            onClick={() => {
                              if (category._id) {
                                setCategoryToDelete(category._id)
                                setShowDeleteConfirm(true)
                              }
                            }}
                          >
                            <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
                          </button>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className='d-flex justify-content-between align-items-center flex-wrap'>
                <div className='d-flex flex-wrap py-2 mr-3'>
                  <button
                    className='btn btn-light btn-active-light-primary me-2'
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                  >
                    {intl.formatMessage({ id: 'PAGINATION.PREVIOUS' })}
                  </button>
                  <button
                    className='btn btn-light btn-active-light-primary'
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    {intl.formatMessage({ id: 'PAGINATION.NEXT' })}
                  </button>
                </div>
                <div className='d-flex align-items-center py-3'>
                  <span className='text-muted'>
                    {intl.formatMessage(
                      { id: 'PAGINATION.INFO' },
                      {
                        page: page + 1,
                        total: totalPages,
                      }
                    )}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Form Modal */}
      {showModal && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(15, 23, 42, 0.45)', zIndex: 1050}}
        >
          <div className='modal-dialog modal-dialog-centered modal-dialog-scrollable mw-650px'>
            <div className='modal-content border-0 rounded-4 shadow-lg'>
              <div className='modal-header border-0 pt-6 px-8 pb-2'>
                <h5 className='modal-title fs-3 fw-bold text-gray-900'>
                  {editingCategory ? intl.formatMessage({id: 'TOUR_CATEGORY.EDIT'}) : intl.formatMessage({id: 'TOUR_CATEGORY.ADD_NEW'})}
                </h5>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-active-color-primary'
                  onClick={handleCancel}
                >
                  <i className='bi bi-x-lg fs-3' />
                </button>
              </div>
              <Form onSubmit={formik.handleSubmit}>
                <div className='modal-body px-8 py-6'>
                  <div className='mb-5'>
                    <label className='form-label required'>{intl.formatMessage({id: 'TOUR_CATEGORY.TITLE'})}</label>
                    <input
                      type='text'
                      className={`form-control ${formik.touched.title && formik.errors.title ? 'is-invalid' : ''}`}
                      {...formik.getFieldProps('title')}
                    />
                    {formik.touched.title && formik.errors.title && (
                      <div className='invalid-feedback'>{formik.errors.title}</div>
                    )}
                  </div>

                  <div className='mb-5'>
                    <label className='form-label required'>{intl.formatMessage({id: 'TOUR_CATEGORY.DESCRIPTION'})}</label>
                    <textarea
                      className={`form-control ${formik.touched.description && formik.errors.description ? 'is-invalid' : ''}`}
                      rows={3}
                      {...formik.getFieldProps('description')}
                    />
                    {formik.touched.description && formik.errors.description && (
                      <div className='invalid-feedback'>{formik.errors.description}</div>
                    )}
                  </div>

                  <div className='mb-5'>
                    <label className='form-label required'>{intl.formatMessage({id: 'TOUR_CATEGORY.STATUS'})}</label>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        type='checkbox'
                        className='form-check-input'
                        checked={formik.values.status === 'active'}
                        onChange={(e) => formik.setFieldValue('status', e.target.checked ? 'active' : 'inactive')}
                      />
                      <label className='form-check-label'>
                        {formik.values.status === 'active' ? 'Active' : 'Inactive'}
                      </label>
                    </div>
                  </div>

                  {/* Translations Section */}
                  <div className='mb-5'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <label className='form-label'>{intl.formatMessage({id: 'TOUR_CATEGORY.TRANSLATIONS'})}</label>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary'
                        onClick={handleAddTranslation}
                      >
                        <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                        {intl.formatMessage({id: 'TOUR_CATEGORY.ADD_TRANSLATION'})}
                      </button>
                    </div>

                    {formik.values.translations.map((translation, index) => (
                      <div key={index} className='card card-custom mb-5'>
                        <div className='card-header'>
                          <div className='card-title'>
                            <div className='d-flex align-items-center'>
                              <select
                                className='form-select form-select-solid me-3'
                                value={translation.language}
                                onChange={(e) => {
                                  const newTranslations = [...formik.values.translations]
                                  newTranslations[index].language = e.target.value
                                  formik.setFieldValue('translations', newTranslations)
                                }}
                              >
                                {AVAILABLE_LANGUAGES.map((lang) => (
                                  <option
                                    key={lang}
                                    value={lang}
                                    disabled={formik.values.translations.some(
                                      (t, i) => i !== index && t.language === lang
                                    )}
                                  >
                                    {lang.toUpperCase()}
                                  </option>
                                ))}
                              </select>
                              <button
                                type='button'
                                className='btn btn-icon btn-light-danger btn-sm'
                                onClick={() => handleRemoveTranslation(index)}
                              >
                                <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className='card-body'>
                          <div className='mb-5'>
                            <label className='form-label required'>{intl.formatMessage({id: 'TOUR_CATEGORY.TRANSLATION_TITLE'})}</label>
                            <input
                              type='text'
                              className='form-control'
                              value={translation.title}
                              onChange={(e) => {
                                const newTranslations = [...formik.values.translations]
                                newTranslations[index].title = e.target.value
                                formik.setFieldValue('translations', newTranslations)
                              }}
                            />
                          </div>
                          <div className='mb-5'>
                            <label className='form-label required'>{intl.formatMessage({id: 'TOUR_CATEGORY.TRANSLATION_DESCRIPTION'})}</label>
                            <textarea
                              className='form-control'
                              rows={3}
                              value={translation.description}
                              onChange={(e) => {
                                const newTranslations = [...formik.values.translations]
                                newTranslations[index].description = e.target.value
                                formik.setFieldValue('translations', newTranslations)
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className='modal-footer border-0 pt-0 pb-7 px-8 d-flex justify-content-end gap-3'>
                  <button type='button' className='btn btn-light-primary fw-semibold px-6' onClick={handleCancel}>
                    {intl.formatMessage({id: 'ACTION.CANCEL'})}
                  </button>
                  <button type='submit' className='btn btn-primary fw-semibold px-8' disabled={formik.isSubmitting}>
                    {formik.isSubmitting && (
                      <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                    )}
                    {editingCategory
                      ? intl.formatMessage({id: 'ACTION.SAVE'})
                      : intl.formatMessage({id: 'ACTION.CREATE'})}
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(15, 23, 42, 0.45)', zIndex: 1050}}
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content border-0 rounded-4 shadow-lg'>
              <div className='modal-header border-0 pt-6 px-8 pb-2'>
                <h5 className='modal-title fs-3 fw-bold text-gray-900'>{intl.formatMessage({id: 'TOUR_CATEGORY.DELETE_CONFIRM'})}</h5>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-active-color-primary'
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  <i className='bi bi-x-lg fs-3' />
                </button>
              </div>
              <div className='modal-body px-8 py-6'>
                <p>{intl.formatMessage({id: 'TOUR_CATEGORY.DELETE_WARNING'})}</p>
              </div>
              <div className='modal-footer border-0 pt-0 pb-7 px-8 d-flex justify-content-end gap-3'>
                <button
                  type='button'
                  className='btn btn-light-primary fw-semibold px-6'
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  {intl.formatMessage({id: 'ACTION.CANCEL'})}
                </button>
                <button
                  type='button'
                  className='btn btn-danger fw-semibold px-6'
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting && (
                    <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                  )}
                  {intl.formatMessage({id: 'ACTION.DELETE'})}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CategoriesTab 